// src/components/heatmaps/ImageDropzone.jsx

import { useRef, useState, useContext, useEffect } from "react";
import { AppContext } from "../../context/AppContext";
import styles from "../../css/heatmaps/ImageDropzone.module.css";
import axiosInstance from "../../api/AxiosConfig";
import ImagesUploaded from "./ImagesUploaded";
import ResultsSection from "./ResultsSection";
import SimpleAlert from "../SimpleAlert";
import ModalLoading from "../ModalLoading";
import uploadIcon from "../../img/banner/upload.svg";
import alertIcon from "../../img/icons/alert.svg";
import { ReactSVG } from "react-svg";
import Pack from "../Pack";

const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["image/png", "image/jpeg"];
const MAX_FILES = 10;

const ImageDropzone = ({ availableCredits = 0, onBlockedSubmit }) => {
  const {
    setImageFileCount,
    setIsUserDataLoading,
    setShowModalCredit,
    setUserData,
  } = useContext(AppContext);

  const fileInputRef = useRef(null);
  const rectRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [resultData, setResultData] = useState(null); // Přejmenováno na resultData pro srozumitelnost
  const [message, setMessage] = useState("");

  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ text: "", type: "base" });

  useEffect(() => {
    setImageFileCount(selectedFiles.length);
  }, [selectedFiles, setImageFileCount]);

  const showAlert = (text, type = "base") => {
    setAlertMessage({ text, type });
    setIsAlertVisible(true);
  };

  const closeAlert = () => {
    setIsAlertVisible(false);
  };

  const handleFileBtnClick = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ... ostatní handlery (handleMouseEnter, handleMouseLeave, atd.) zůstávají stejné ...
  const handleMouseEnter = () => {
    if (!rectRef.current || isUploading) return;
    const rect = rectRef.current;
    rect.style.animation = "";
    rect.classList.remove(styles.isAnimating);
    void rect.getBoundingClientRect();
    rect.classList.add(styles.isAnimating);
  };

  const handleMouseLeave = () => {
    if (!rectRef.current || isUploading) return;
    const rect = rectRef.current;
    rect.style.animation = "none";
    const currentOffset = parseFloat(
      window.getComputedStyle(rect).strokeDashoffset
    );
    rect.style.transition = "none";
    rect.style.strokeDashoffset = currentOffset;
    rect.classList.remove(styles.isAnimating);
    requestAnimationFrame(() => {
      rect.style.transition = "stroke-dashoffset 500ms linear";
      rect.style.strokeDashoffset = currentOffset + 50;
    });
  };

  const handleFiles = (files) => {
    const validFiles = [];
    const errorMessages = [];
    const existingFileIds = new Set(
      selectedFiles.map((f) => `${f.name}-${f.size}`)
    );

    // Respect maximum file count
    const remainingSlots = Math.max(0, MAX_FILES - selectedFiles.length);
    if (remainingSlots === 0) {
      showAlert(`Je povoleno nahrát maximálně ${MAX_FILES} obrázků.`, "danger");
      return;
    }

    for (const file of files) {
      const fileId = `${file.name}-${file.size}`;

      if (existingFileIds.has(fileId)) {
        continue;
      }

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        errorMessages.push(`• ${file.name}: Nepodporovaný formát.`);
        continue;
      }

      if (file.size > MAX_SIZE_BYTES) {
        errorMessages.push(
          `• ${file.name}: Soubor je příliš velký (max ${MAX_SIZE_MB} MB).`
        );
        continue;
      }

      validFiles.push(file);
    }

    if (errorMessages.length > 0) {
      showAlert(errorMessages.join("\n"), "danger");
    }

    // Enforce the cap when adding
    if (validFiles.length > 0) {
      const toAdd = validFiles.slice(0, remainingSlots);
      if (toAdd.length < validFiles.length) {
        const skipped = validFiles.length - toAdd.length;
        showAlert(
          `Dosažen limit ${MAX_FILES} obrázků. Přidáno ${toAdd.length}, přeskočeno ${skipped}.`,
          "danger"
        );
      }
      setSelectedFiles((prev) => [...prev, ...toAdd]);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploading) return;
    setIsDragging(true);
    handleMouseEnter();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleMouseLeave();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleMouseLeave();
    if (isUploading) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileChange = (e) => {
    if (!e.target.files) return;
    handleFiles(Array.from(e.target.files));
    if (e.target) {
      e.target.value = null;
    }
  };

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const selectedCount = Array.isArray(selectedFiles) ? selectedFiles.length : 0;

  const canSubmit =
    availableCredits > 0 &&
    selectedCount > 0 &&
    selectedCount <= availableCredits &&
    selectedCount <= MAX_FILES;

  const isCreditBlocked =
    selectedCount > 0 &&
    (availableCredits <= 0 || selectedCount > availableCredits);

  const blockSubmit = (msg) => {
    if (onBlockedSubmit) onBlockedSubmit(msg);
    showAlert(msg, "danger");
  };

  const handleUploadToServer = async () => {
    if (selectedCount === 0) {
      showAlert("Nejprve vyberte soubory.", "info");
      return;
    }

    if (selectedCount > MAX_FILES) {
      showAlert(
        `Je povoleno odeslat maximálně ${MAX_FILES} obrázků.`,
        "danger"
      );
      return;
    }

    if (availableCredits <= 0) {
      return blockSubmit(
        "Nemáte žádné kredity. Dobijte si prosím kredit a zkuste to znovu."
      );
    }

    if (selectedCount > availableCredits) {
      return blockSubmit(
        `Máte vybráno ${selectedCount} obrázků, ale k dispozici je ${availableCredits} kreditů.`
      );
    }
    setIsUploading(true);
    setIsUserDataLoading(true);

    try {
      // Konverze souborů na base64 s progress tracking
      const base64Arr = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setMessage(
          `Konvertuji obrázek: ${file.name} (${i + 1}/${selectedFiles.length})`
        );
        const base64Data = await convertFileToBase64(file);
        base64Arr.push(base64Data);
      }

      // Příprava payload
      setMessage("Odesílám data na server...");
      const payload = {
        images: base64Arr.map((data, idx) => ({
          image_data: data,
          threshold_percent: 90.0,
          image_id: selectedFiles[idx].name,
        })),
      };

      setMessage("Generuji heatmapy...");
      const resp = await axiosInstance.post("generate_heatmaps/", payload);

      if (Array.isArray(resp.data.results) && resp.data.results.length > 0) {
        setResultData(resp.data.results);
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Po úspěšné analýze odečti kredity lokálně (bez API volání user/update)
        const analyzedCount = resp?.data?.results?.length ?? selectedCount;
        setUserData((prev) => {
          const prevCredits = Math.max(
            0,
            Number(prev?.available_credits ?? availableCredits ?? 0)
          );
          const newCredits = Math.max(0, prevCredits - analyzedCount);
          return { ...prev, available_credits: newCredits };
        });
        // showAlert(
        //   "Zpracování proběhlo úspěšně! Výsledky naleznete níže.",
        //   "info"
        // );
      } else {
        showAlert("Server vrátil neočekávaný formát dat.", "danger");
      }
    } catch (err) {
      console.error(err);
      showAlert(`Došlo k chybě: ${err.message}`, "danger");
    } finally {
      setIsUploading(false);
      setIsUserDataLoading(false);
      setMessage("");
    }
  };

  const handleRemoveImage = (fileToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter(
        (f) => f.name !== fileToRemove.name || f.size !== fileToRemove.size
      )
    );
  };

  const handleRemoveAll = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    showAlert("Všechny obrázky byly odstraněny.", "info");
  };

  return (
    <>
      {isAlertVisible && (
        <SimpleAlert
          message={alertMessage.text}
          onClose={closeAlert}
          type={alertMessage.type}
        />
      )}

      <Pack dataComponent={"image-dropzone"}>
        <div className={styles.dropzoneContainer}>
          <div
            className={`${styles.dropzone} ${
              isDragging ? styles.isDragging : ""
            }`}
            onClick={handleFileBtnClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ cursor: isUploading ? "wait" : "pointer" }}
          >
            <svg className={styles.dropzoneSvg} width="100%" height="100%">
              <rect
                ref={rectRef}
                x="1.5"
                y="1.5"
                rx="16"
                ry="16"
                width="calc(100% - 3px)"
                height="calc(100% - 3px)"
              />
            </svg>
            <div className={styles.dropzoneContent}>
              <ReactSVG src={uploadIcon} />
              <h3>Přetáhněte zde banner</h3>
              <p>nebo</p>
              <p className={styles.chooseText}>vyberte soubor</p>
              <input
                hidden
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_MIME_TYPES.join(",")}
                multiple
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <p className={styles.instructions}>
                Maximální velikost souboru: <strong>{MAX_SIZE_MB} MB</strong>
                <br />
                Podporované formáty: <strong>JPG, PNG</strong>
              </p>
              {/** Info o kreditech a výběru záměrně nezobrazujeme na přání. **/}
            </div>
          </div>
          {selectedFiles.length > 0 && (
            <div className={styles.imagesListWrapper}>
              <ImagesUploaded
                images={selectedFiles}
                handleUploadToServer={handleUploadToServer}
                handleRemoveImage={handleRemoveImage}
                handleRemoveAll={handleRemoveAll}
                canSubmit={canSubmit}
                submitDisabledReason={
                  selectedCount === 0
                    ? "Nejprve vyberte soubory."
                    : availableCredits <= 0
                    ? "Nemáte žádné kredity."
                    : selectedCount > availableCredits
                    ? `Vybráno ${selectedCount}, ale jen ${availableCredits} kreditů.`
                    : ""
                }
              />
            </div>
          )}

          {selectedCount > 0 &&
            (availableCredits <= 0 || selectedCount > availableCredits) && (
              <div
                className={styles.creditWarning}
                role="region"
                aria-live="polite"
              >
                <div className={styles.creditWarningInner}>
                  <span className={styles.creditWarningIcon} aria-hidden="true">
                    <img src={alertIcon} alt="" />
                  </span>
                  <p className={styles.creditWarningText}>
                    Nemáte dostatek kreditů. Dokupte si kredity, abyste mohli
                    nadále testovat své bannery.
                  </p>
                  <button
                    type="button"
                    className={styles.creditWarningButton}
                    onClick={() => setShowModalCredit(true)}
                  >
                    Dokoupit kredity
                  </button>
                </div>
              </div>
            )}
        </div>
      </Pack>

      <ResultsSection
        analysisData={resultData}
        onNewAnalysis={() => setResultData(null)}
      />

      {isUploading && <ModalLoading message={message} />}
    </>
  );
};

export default ImageDropzone;
